<template>
  <div 
    class="image-zoom-container" 
    ref="container"
    @mousemove="handleMouseMove"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @contextmenu.prevent
  >
    <img 
      :src="src" 
      :alt="alt" 
      class="base-image" 
      :class="{ 'is-zoomed': isZoomed }"
      ref="image"
    >
    <div 
      v-if="isZoomed" 
      class="zoom-lens"
      :style="lensStyle"
    ></div>
    <div 
      v-if="isZoomed" 
      class="zoom-result"
      :class="{ 'mobile-zoom': isMobile }"
      :style="resultStyle"
    ></div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  src: {
    type: String,
    required: true
  },
  alt: {
    type: String,
    default: ''
  },
  zoomScale: {
    type: Number,
    default: 2.5
  }
})

const container = ref(null)
const image = ref(null)
const isZoomed = ref(false)
const isMobile = ref(false)

const pos = reactive({
  x: 0,
  y: 0,
  lensX: 0,
  lensY: 0,
  bgX: 0,
  bgY: 0
})

const lensSize = 150

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
  
  // Aggiungiamo i listener touch manualmente per avere il controllo su 'passive'
  if (container.value) {
    container.value.addEventListener('touchstart', handleTouchStart, { passive: false })
    container.value.addEventListener('touchmove', handleTouchMove, { passive: false })
    container.value.addEventListener('touchend', handleTouchEnd)
    container.value.addEventListener('touchcancel', handleTouchEnd)
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
  if (container.value) {
    container.value.removeEventListener('touchstart', handleTouchStart)
    container.value.removeEventListener('touchmove', handleTouchMove)
    container.value.removeEventListener('touchend', handleTouchEnd)
    container.value.removeEventListener('touchcancel', handleTouchEnd)
  }
})

const handleMouseEnter = () => {
  if (!isMobile.value) {
    isZoomed.value = true
  }
}

const handleMouseLeave = () => {
  isZoomed.value = false
}

const handleMouseMove = (e) => {
  if (isMobile.value || !isZoomed.value) return
  updateZoom(e.clientX, e.clientY)
}

const handleTouchStart = (e) => {
  if (e.touches.length > 1) return
  
  // Blocco immediato per il carosello
  e.stopImmediatePropagation()
  
  isZoomed.value = true
  updateZoom(e.touches[0].clientX, e.touches[0].clientY)
}

const handleTouchMove = (e) => {
  if (!isZoomed.value) return
  
  // Continua a bloccare il carosello e lo scroll
  e.stopImmediatePropagation()
  if (e.cancelable) e.preventDefault()
  
  updateZoom(e.touches[0].clientX, e.touches[0].clientY)
}

const handleTouchEnd = (e) => {
  if (e) e.stopImmediatePropagation()
  isZoomed.value = false
}

const updateZoom = (clientX, clientY) => {
  if (!container.value) return

  const rect = container.value.getBoundingClientRect()
  
  // Coordinate relative
  let x = clientX - rect.left
  let y = clientY - rect.top

  // Su mobile, applichiamo un piccolo offset verso l'alto 
  // per non coprire il punto di zoom con il dito
  if (isMobile.value) {
    y -= 40 
  }

  // Clamp values
  x = Math.max(0, Math.min(x, rect.width))
  y = Math.max(0, Math.min(y, rect.height))

  pos.x = x
  pos.y = y

  // Calcolo posizione lente (centrata sul cursore/dito)
  pos.lensX = x - (lensSize / 2)
  pos.lensY = y - (lensSize / 2)

  // Mantieni lente nei bordi
  pos.lensX = Math.max(0, Math.min(pos.lensX, rect.width - lensSize))
  pos.lensY = Math.max(0, Math.min(pos.lensY, rect.height - lensSize))

  // Percentuali per lo sfondo
  pos.bgX = (x / rect.width) * 100
  pos.bgY = (y / rect.height) * 100
}

const lensStyle = computed(() => ({
  left: `${pos.lensX}px`,
  top: `${pos.lensY}px`,
  width: `${lensSize}px`,
  height: `${lensSize}px`,
  opacity: isZoomed.value ? 1 : 0,
  pointerEvents: 'none'
}))

const resultStyle = computed(() => ({
  backgroundImage: `url(${props.src})`,
  backgroundPosition: `${pos.bgX}% ${pos.bgY}%`,
  backgroundSize: `${100 * props.zoomScale}%`,
  opacity: isZoomed.value ? 1 : 0,
  pointerEvents: 'none',
  zIndex: isMobile.value ? 200 : 20 // Porta il risultato sopra tutto su mobile
}))
</script>

<style scoped>
.image-zoom-container {
  position: relative;
  width: 100%;
  height: 100%;
  cursor: zoom-in;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  touch-action: pan-y; /* Permette lo scroll verticale della pagina */
}

.base-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  image-rendering: high-quality;
  image-rendering: -webkit-optimize-contrast;
  transition: transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), filter 0.3s ease;
  will-change: transform;
  transform: translateZ(0);
  pointer-events: auto; /* L'immagine deve ricevere il tocco */
}

.base-image.is-zoomed {
  filter: blur(2px) grayscale(20%);
  transform: scale(1.02);
}

.zoom-lens {
  position: absolute;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background-color: rgba(255, 255, 255, 0.1);
  pointer-events: none;
  z-index: 10;
  backdrop-filter: brightness(1.1);
  transition: opacity 0.3s ease;
}

.zoom-result {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 20;
  background-repeat: no-repeat;
  background-color: #fff;
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Premium smooth entry */
@keyframes zoomFadeIn {
  from { opacity: 0; transform: scale(1.05); }
  to { opacity: 1; transform: scale(1); }
}

.zoom-result[style*="opacity: 1"] {
  animation: zoomFadeIn 0.4s cubic-bezier(0.23, 1, 0.32, 1);
}

.zoom-result.mobile-zoom {
  z-index: 100;
  border: none;
}

.mobile-hint {
  position: absolute;
  bottom: 15px;
  right: 15px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(4px);
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
  pointer-events: none;
  opacity: 0.6;
  animation: pulseHint 2s infinite;
}

@keyframes pulseHint {
  0% { transform: scale(1); opacity: 0.6; }
  50% { transform: scale(1.1); opacity: 0.8; }
  100% { transform: scale(1); opacity: 0.6; }
}

@media (max-width: 768px) {
  .image-zoom-container {
    cursor: default;
    touch-action: pan-y; /* Permette lo scroll verticale ma blocca quello orizzontale se serve */
  }
}
</style>
